import {naturalSort} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import produce from 'immer';
import React, {ReactNode, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {Collaborator, OwnerInfo} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {Label} from 'bulk-workflows/BulkWorkflowComponents';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import {
  ReviewProcessSelectorQuery,
  ReviewProcessSelectorQueryVariables,
} from 'notebook/reviews/review-lifecycles/__generated/ReviewProcessSelectorQuery';
import {
  formatListWithBoldedNames,
  getRlcTeamsWithoutMemberAccessToProject,
} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {BLUE_LIGHT, GRAY, WHITE} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import sortedBy from 'util/sortedBy';
import {InputChangeEvent} from 'util/Types';

import WithReviewProcessHelpLink, {ReviewLifecycleHelpArticle} from './WithReviewLifecycleHelpLink';

type ReviewLifecycleGraphQL = ExtractByTypename<
  ReviewProcessSelectorQuery['reviewLifecycles'][number],
  'ReviewLifecycleNode'
>;

type OrganizationGraphQL = ReviewLifecycleGraphQL['organization'];

type ReviewStageTeamGraphQL = NonNullable<
  ReviewLifecycleGraphQL['currentVersion']['reviewStages'][number]['selectedTeam']
>;

export const LEGACY_REVIEW_PROCESS_ID = 'LEGACY_REVIEW_PROCESS_OPTION';

function RequiredAsterisk(): JSX.Element {
  return <span className="u-danger-color">*</span>;
}

const styles = StyleSheet.create({
  inlineBanner: {
    marginTop: '8px',
  },
  selectedCheckIcon: {color: BLUE_LIGHT},
  unselectedCheckIcon: {color: GRAY},
  auditorReviewCheckbox: {display: 'flex', flexDirection: 'row', marginTop: '10px'},
  auditorCheckboxHelp: {marginLeft: '4px'},
  // This prevents clicking on the header option from making the background look as if it's selected.
  multiDropdownHeaderOption: {backgroundColor: WHITE},
});

export function generateReviewProcessSelectorOptions({
  reviewLifecycleIdAndNames,
  selectedReviewLifecycleIds,
  isMulti,
  sortByName = true,
}: {
  reviewLifecycleIdAndNames: Array<{id: string; name: string}>;
  selectedReviewLifecycleIds: Array<string>;
  isMulti: boolean;
  sortByName?: boolean;
}): Array<OptionData> {
  const options = reviewLifecycleIdAndNames.map(
    ({id, name}) =>
      ({
        value: id,
        name,
        isHeader: false,
        icon: isMulti ? 'check' : undefined,
        iconClassName: isMulti
          ? selectedReviewLifecycleIds.includes(id)
            ? css(styles.selectedCheckIcon)
            : css(styles.unselectedCheckIcon)
          : undefined,
      }) satisfies OptionData
  );

  return sortByName ? sortedBy(options, ['name']) : options;
}

export const REVIEW_PROCESS_SELECTOR_QUERY = gql`
  query ReviewProcessSelectorQuery($reviewLifecycleIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewLifecycles(ids: $reviewLifecycleIds) {
      ... on ReviewLifecycleNode {
        id
        organization {
          id
          name
        }
        currentVersion {
          id
          name
          reviewStages {
            id
            selectedTeam {
              id
              name
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function ReviewProcessSelectorBanner({
  text,
  bannerType,
}: {
  text: ReactNode;
  bannerType: InlineBannerType;
}): JSX.Element {
  return (
    <div className={css(styles.inlineBanner)}>
      <InlineBanner bannerType={bannerType} supportingInfo={text} />
    </div>
  );
}

export function EditWarning({
  initialReviewLifecycles,
}: {
  initialReviewLifecycles: Array<ReviewLifecycleGraphQL>;
}): JSX.Element {
  const reviewableResetMsg = BenchlingConfig.ENABLE_RETRACT_ACTIVE_REVIEWS_ON_REVIEW_PROCESS_UPDATE ? (
    <div>
      Items currently <b>In Review</b> using removed Review Processes will be reset to&nbsp;
      <b>In Progress</b>&nbsp;and must be resent for review.
    </div>
  ) : (
    <div>
      Items currently <b>In Review</b> will still use the previous review process.
    </div>
  );

  let revertReviewProcessMsg = (
    <div>
      <br />
      Check <b>Use auditor review</b> to undo changes.
    </div>
  );
  if ((initialReviewLifecycles ?? []).length > 0) {
    const rlcNames = formatListWithBoldedNames(
      initialReviewLifecycles.map(({currentVersion: {name}}) => name)
    );
    revertReviewProcessMsg = (
      <div>
        <br />
        {/* eslint-disable-next-line react/no-danger */}
        Revert back to <span dangerouslySetInnerHTML={{__html: rlcNames}} /> (from{' '}
        <b>{initialReviewLifecycles[0].organization.name}</b>) to undo changes.
      </div>
    );
  }

  const fewMinutesMsg = BenchlingConfig.ENABLE_RETRACT_ACTIVE_REVIEWS_ON_REVIEW_PROCESS_UPDATE ? (
    <div>This change may take a few minutes to apply.</div>
  ) : (
    <div>Updating this project's review process may take a few minutes.</div>
  );

  const message = (
    <div>
      {reviewableResetMsg}
      {fewMinutesMsg}
      {revertReviewProcessMsg}
    </div>
  );

  return <ReviewProcessSelectorBanner bannerType={InlineBannerType.WARNING} text={message} />;
}

/** @package */
export function NoAccessTeamsWarning({
  missingTeams,
  allTeams,
  reviewOrgName,
  reviewLifecycleName,
}: {
  allTeams: Array<{id: string; name: string}>;
  reviewOrgName: string;
  reviewLifecycleName: string;
  missingTeams: Array<{id: string; name: string}>;
}): JSX.Element {
  const missingTeamNames = missingTeams.map(({name}) => name);
  const missingTeamNamesString = formatListWithBoldedNames(missingTeamNames);
  const message = `${getTeamReviewerMessageForOrgString(
    allTeams,
    reviewOrgName,
    reviewLifecycleName
  )}<br><br>${missingTeamNamesString} ${pluralize(
    missingTeamNames.length,
    'has',
    'have'
  )} not been given explicit access to this project.`;
  return (
    <ReviewProcessSelectorBanner
      bannerType={InlineBannerType.WARNING}
      // eslint-disable-next-line react/no-danger
      text={<div dangerouslySetInnerHTML={{__html: message}} />}
    />
  );
}

/** @package */
export function TeamsArePresentInfo({
  reviewStageTeams,
  reviewOrgName,
  reviewLifecycleName,
}: {
  reviewStageTeams: Array<{id: string; name: string}>;
  reviewOrgName: string;
  reviewLifecycleName: string;
}): JSX.Element {
  const teamReviewers = getTeamReviewerMessageForOrg(reviewStageTeams, reviewOrgName, reviewLifecycleName);
  return <ReviewProcessSelectorBanner bannerType={InlineBannerType.INFO} text={teamReviewers} />;
}

function getTeamReviewerMessageForOrgString(
  reviewStageTeams: Array<{id: string; name: string}>,
  reviewOrgName: string,
  reviewLifecycleName: string
): string | null {
  let message = '';

  // Dedupe
  const reviewStageTeamNames = Array.from(new Set(reviewStageTeams.map((team) => team.name)));

  if (reviewStageTeamNames.length > 0) {
    message += formatListWithBoldedNames(reviewStageTeamNames);
    message += ` (from <b>${reviewOrgName}</b>) ${pluralize(
      reviewStageTeamNames.length,
      'is a reviewer',
      'are reviewers'
    )} of <b>${reviewLifecycleName}</b>.`;
    return message;
  }
  return null;
}

function getTeamReviewerMessageForOrg(
  reviewStageTeams: Array<{id: string; name: string}>,
  reviewOrgName: string,
  reviewLifecycleName: string
): ReactNode | null {
  const message = getTeamReviewerMessageForOrgString(reviewStageTeams, reviewOrgName, reviewLifecycleName);
  if (message) {
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: message,
        }}
      />
    );
  }
  return null;
}

interface ReviewProcessSelectorWarningsProps {
  initialReviewLifecycles: Array<ReviewLifecycleGraphQL>;
  reviewLifecycles: Array<ReviewLifecycleGraphQL>;
  collaborators: Array<Collaborator>;
  projectOwnerId: string;
  showEditWarning?: boolean;
  showTeamWarnings?: boolean;
  projectOwnerInfo: OwnerInfo;
}

function ReviewProcessSelectorWarnings({
  initialReviewLifecycles,
  reviewLifecycles,
  collaborators,
  projectOwnerId,
  showEditWarning,
  showTeamWarnings,
  projectOwnerInfo,
}: ReviewProcessSelectorWarningsProps): JSX.Element {
  const reviewStageTeamsPerReviewLifecycle: Array<{
    reviewStageTeams: Array<ReviewStageTeamGraphQL>;
    noAccessTeams: Array<{id: string; name: string}>;
    reviewOrg: OrganizationGraphQL;
    reviewLifecycleName: string;
    reviewLifecycleId: string;
  }> = reviewLifecycles.map((reviewLifecycle) => {
    const reviewStageTeams =
      reviewLifecycle.currentVersion.reviewStages
        .map(({selectedTeam}) => selectedTeam)
        .filter((team): team is ReviewStageTeamGraphQL => team != null) ?? [];
    return {
      reviewOrg: reviewLifecycle.organization,
      reviewStageTeams,
      noAccessTeams:
        reviewStageTeams.length > 0
          ? getRlcTeamsWithoutMemberAccessToProject(
              reviewStageTeams,
              collaborators,
              reviewLifecycle.organization.id,
              projectOwnerId,
              projectOwnerInfo
            )
          : [],
      reviewLifecycleName: reviewLifecycle.currentVersion.name,
      reviewLifecycleId: reviewLifecycle.id,
    };
  });

  return (
    <>
      {showEditWarning && <EditWarning initialReviewLifecycles={initialReviewLifecycles} />}
      {reviewStageTeamsPerReviewLifecycle.map(
        ({reviewStageTeams, noAccessTeams, reviewOrg, reviewLifecycleName, reviewLifecycleId}) =>
          showTeamWarnings && reviewStageTeams.length + noAccessTeams.length > 0 ? (
            <div key={reviewLifecycleId}>
              {noAccessTeams.length === 0 ? (
                <TeamsArePresentInfo
                  reviewStageTeams={reviewStageTeams}
                  reviewOrgName={reviewOrg.name}
                  reviewLifecycleName={reviewLifecycleName}
                />
              ) : (
                <NoAccessTeamsWarning
                  allTeams={reviewStageTeams}
                  reviewOrgName={reviewOrg.name}
                  reviewLifecycleName={reviewLifecycleName}
                  missingTeams={noAccessTeams}
                />
              )}
            </div>
          ) : undefined
      )}
    </>
  );
}

interface MultiReviewProcessSelectorProps {
  selectedReviewLifecycleIds: Array<string>;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  removeReviewLifecycleId: (reviewLifecycleId: string) => void;
  options: Array<OptionData>;
  isReadOnly?: boolean;
}

function MultiReviewProcessSelector({
  isReadOnly,
  options,
  selectReviewLifecycleId,
  selectedReviewLifecycleIds,
  removeReviewLifecycleId,
}: MultiReviewProcessSelectorProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');

  const optionsFilteredBySearchQuery = options.filter((option) =>
    (option.name as string).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ChipSelectInput
      autoFocus={false}
      disabled={isReadOnly}
      optionDatas={optionsFilteredBySearchQuery}
      placeholder={selectedReviewLifecycleIds?.length === 0 ? 'Select a review process...' : ''}
      value={selectedReviewLifecycleIds}
      onSelect={(value: OptionValue) => {
        const isHeader = optionsFilteredBySearchQuery.find((option) =>
          isEqual(option.value, value)
        )?.isHeader;
        if (!isHeader) {
          selectReviewLifecycleId(value);
          setSearchQuery('');
        }
      }}
      onRemove={removeReviewLifecycleId}
      isMulti={true}
      getChipProps={(optionValue: OptionValue) => ({
        text: optionsFilteredBySearchQuery.find((option) => option.value === optionValue)?.name as string,
      })}
      query={searchQuery}
      onQueryChange={(e) => {
        setSearchQuery(e.target.value);
      }}
      scrollable={true}
    />
  );
}

interface SingleReviewProcessSelectorProps {
  selectedReviewLifecycleId?: string;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  options: Array<OptionData>;
  isReadOnly?: boolean;
}

function SingleReviewProcessSelector({
  isReadOnly,
  options,
  selectReviewLifecycleId,
  selectedReviewLifecycleId,
}: SingleReviewProcessSelectorProps): JSX.Element {
  return (
    <SelectDropdown
      disabled={isReadOnly}
      menu={
        <SearchableMenu
          selectedValue={
            options.find((option) => option.value === selectedReviewLifecycleId)?.name ?? undefined
          }
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          scrollable={true}
          optionDatas={options}
          onSelect={selectReviewLifecycleId}
        />
      }
      displayName={
        selectedReviewLifecycleId
          ? (options.find((option) => option.value === selectedReviewLifecycleId)?.name ?? options[0].name)
          : 'Select a review process...'
      }
      dataTestAttrs={{component: 'SelectReviewProcessDropdown', element: 'selectDropdown'}}
    />
  );
}

interface ReviewProcessSelectorWithWarnings {
  isRequired?: boolean;
  isAuditorBasedReview?: boolean;
  includeAuditorBasedReviewOption: boolean;
  setIsAuditorBasedReview?: (isAuditorBasedReview: boolean) => void;
  showWarnings: true;
  className: 'well';
  labelClassName?: string;
  initialReviewLifecycleIds?: Array<string>;
  showWarningMessageOnReviewLifecycleEdit?: boolean;
  collaborators: Array<Collaborator>;
  projectOwnerId: string;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  removeReviewLifecycleId?: (reviewLifecycleId: string) => void;
  options: Array<OptionData>;
  selectedReviewLifecycleIds: Array<string>;
  isReadOnly?: boolean;
  ownerInfo: OwnerInfo;
  isMulti?: boolean;
}

interface ReviewProcessSelectorWithoutWarnings {
  isRequired?: boolean;
  isAuditorBasedReview?: boolean;
  includeAuditorBasedReviewOption: boolean;
  setIsAuditorBasedReview?: (isAuditorBasedReview: boolean) => void;
  showWarnings: false;
  className?: string;
  labelClassName?: string;
  initialReviewLifecycleIds?: never;
  showWarningMessageOnReviewLifecycleEdit?: never;
  collaborators?: never;
  projectOwnerId?: never;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  removeReviewLifecycleId?: (reviewLifecycleId: string) => void;
  options: Array<OptionData>;
  selectedReviewLifecycleIds: Array<string>;
  isReadOnly?: boolean;
  ownerInfo?: never;
  isMulti?: boolean;
}

type ReviewProcessSelectorProps = ReviewProcessSelectorWithWarnings | ReviewProcessSelectorWithoutWarnings;

function injectDataTestAttrsIntoOptions(options: Array<OptionData>): Array<OptionData> {
  return produce(options, (draftOptions) => {
    draftOptions.forEach((option) => {
      option.dataTestAttrs = {
        component: 'SelectReviewProcessDropdown',
        element: 'selectOption',
        key: option.value,
      };
    });
  });
}

function shouldShowEditWarning(
  showWarningMessageOnReviewLifecycleEdit: boolean,
  selectedReviewLifecycleIds: Array<string>,
  initialReviewLifecycleIds: Array<string>,
  isAuditorBasedReview: boolean
): boolean {
  let showEditWarning = false;
  if (showWarningMessageOnReviewLifecycleEdit) {
    showEditWarning = true;
    if ((initialReviewLifecycleIds ?? []).length === 0) {
      // If it was initially legacy review, then only render the edit warning if it's not anymore
      showEditWarning = !isAuditorBasedReview;
    } else {
      // If it's currently set to legacy, show edit warning. If it's not, compute the difference between
      // previously selected and currently selected review processes.
      showEditWarning =
        isAuditorBasedReview ||
        !isEqual(naturalSort(selectedReviewLifecycleIds), naturalSort(initialReviewLifecycleIds ?? []));
    }
  }
  return showEditWarning;
}

/** @package */
export function AuditorCheckbox({
  disabled,
  checked,
  setChecked,
}: {
  disabled: boolean;
  checked: boolean;
  setChecked: (checked: boolean) => void;
}): JSX.Element {
  return (
    <div
      className={css(styles.auditorReviewCheckbox)}
      {...getTestProps({component: 'ReviewProcessSelector', element: 'AuditorCheckbox'})}
    >
      <CheckboxInput
        disabled={disabled}
        checked={checked}
        onChange={(e: InputChangeEvent) => {
          setChecked(e.target.checked);
          return null;
        }}
        id="review-process-selector-checkbox"
        label="Use auditor review"
      />
      <div className={css(styles.auditorCheckboxHelp)}>
        <HelpButton
          renderContent={() => (
            <>
              When this option is selected, project admins will be able to elect individual collaborators as
              allowed auditors for review.{' '}
              <WithReviewProcessHelpLink
                helpArticle={ReviewLifecycleHelpArticle.ADD_AUDITORS_FOR_NOTEBOOK_REVIEWS}
              >
                Learn more
              </WithReviewProcessHelpLink>
            </>
          )}
        />
      </div>
    </div>
  );
}

/**
 * Renders a dropdown with Review Process options. It currently supports selecting from the current review
 * process that exists and any review lifecycles that are accessible to the current entity.
 */
export default function ReviewProcessSelector({
  isRequired = false,
  isMulti = false,
  showWarnings,
  className = '',
  labelClassName,
  options,
  selectedReviewLifecycleIds,
  isAuditorBasedReview,
  setIsAuditorBasedReview,
  includeAuditorBasedReviewOption,
  initialReviewLifecycleIds,
  selectReviewLifecycleId,
  removeReviewLifecycleId,
  showWarningMessageOnReviewLifecycleEdit,
  isReadOnly,
  collaborators,
  projectOwnerId,
  ownerInfo,
}: ReviewProcessSelectorProps): JSX.Element | null {
  if (isMulti) {
    assert(
      removeReviewLifecycleId != null,
      'Must provide remove option handler if isMulti is enabled to support removing selected options.'
    );
  }
  if (includeAuditorBasedReviewOption) {
    assert(
      setIsAuditorBasedReview != null,
      'Must provide setter for toggling auditor checkbox if checkbox is rendered.'
    );
    assert(isAuditorBasedReview != null, 'Must provide auditor checkbox state if checkbox is rendered.');
  }

  options = injectDataTestAttrsIntoOptions(options);

  // Fetch all selectable review lifecycles on load to prevent loading indicators from showing up in between selects.
  const reviewLifecycleIdsToQuery = Array.from(
    new Set(
      options
        .map((option) => (!option.isHeader ? option.value : null))
        .filter((id): id is string => id != null)
        .concat(initialReviewLifecycleIds ?? [])
    )
  );
  const result = useSkippableBenchlingQuery<ReviewProcessSelectorQuery, ReviewProcessSelectorQueryVariables>(
    REVIEW_PROCESS_SELECTOR_QUERY,
    {
      variables: {reviewLifecycleIds: reviewLifecycleIdsToQuery},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
      skip: reviewLifecycleIdsToQuery.length === 0,
    }
  );

  switch (result.type) {
    case QueryResultType.PENDING:
      return (
        <div className={className ?? 'well'}>
          <CenteredHorizontalThrobber />
        </div>
      );
    case QueryResultType.ERROR:
      return null;

    // SUCCESS or SKIPPED
    default: {
      const reviewLifecycles = result.data?.reviewLifecycles ?? [];
      const accessibleReviewLifecycles = reviewLifecycles.filter(
        (reviewLifecycle): reviewLifecycle is ReviewLifecycleGraphQL => !isInaccessible(reviewLifecycle)
      );
      if (accessibleReviewLifecycles.length !== reviewLifecycles.length) {
        return null;
      }

      const initialReviewLifecycles = accessibleReviewLifecycles.filter((reviewLifecycle) =>
        initialReviewLifecycleIds?.includes(reviewLifecycle.id)
      );

      const selectedReviewLifecycles = accessibleReviewLifecycles.filter(({id}) =>
        selectedReviewLifecycleIds.includes(id)
      );

      const isLegacyReview = Boolean(includeAuditorBasedReviewOption && isAuditorBasedReview);

      const disableSelector = isReadOnly || isLegacyReview;

      return (
        <div className={className}>
          <FormGroup disableBottomMargin={true}>
            <Label className={labelClassName}>Review process</Label>
            {isRequired && <RequiredAsterisk />}
            <div className="input-group u-width-full">
              <div className="collaborator-search-container">
                {isMulti ? (
                  <MultiReviewProcessSelector
                    isReadOnly={disableSelector}
                    options={options}
                    selectReviewLifecycleId={selectReviewLifecycleId}
                    // This allows us to render `Select a review process...` when the auditor checkbox is selected.s
                    selectedReviewLifecycleIds={isLegacyReview ? [] : selectedReviewLifecycleIds}
                    removeReviewLifecycleId={removeReviewLifecycleId!}
                  />
                ) : (
                  <SingleReviewProcessSelector
                    isReadOnly={disableSelector}
                    // This allows us to render `Select a review process...` when the auditor checkbox is selected.
                    selectedReviewLifecycleId={isLegacyReview ? undefined : selectedReviewLifecycleIds?.[0]}
                    options={options}
                    selectReviewLifecycleId={selectReviewLifecycleId}
                  />
                )}

                {includeAuditorBasedReviewOption && (
                  <AuditorCheckbox
                    disabled={Boolean(isReadOnly)}
                    checked={isLegacyReview}
                    // Assertion above to check that `setIsAuditorBasedReview` is not null
                    setChecked={setIsAuditorBasedReview!}
                  />
                )}

                {showWarnings && (
                  <ReviewProcessSelectorWarnings
                    showTeamWarnings={!isAuditorBasedReview}
                    initialReviewLifecycles={initialReviewLifecycles}
                    showEditWarning={shouldShowEditWarning(
                      showWarningMessageOnReviewLifecycleEdit ?? false,
                      selectedReviewLifecycleIds,
                      initialReviewLifecycleIds ?? [],
                      isAuditorBasedReview ?? false
                    )}
                    reviewLifecycles={selectedReviewLifecycles}
                    // TODO: Fix the ! asserts since the types say these could be undefined. Introduced by Typescript 5.1 upgrade.
                    collaborators={collaborators!}
                    projectOwnerId={projectOwnerId!}
                    projectOwnerInfo={ownerInfo!}
                  />
                )}
              </div>
            </div>
          </FormGroup>
        </div>
      );
    }
  }
}
